import _ from 'lodash';
import React, { useMemo } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import ApproveGroupingsContainer from './approve-groupings.container';
import { getAuthUser } from '../../../lib/services/auth.service';
import { IFinalizeSessionFields, IFinalizeSessionProps } from './approve-groupings.interface';
import { readUserInputMessage } from '../../../lib/helpers/graphql-error.helpers';

const GET_APPROVE_GROUPINGS = gql`
  query GetApproveGroupings($assessmentId: ID!, $companyId: ID!, $ageGroupId: ID!, $includeGlobal: Boolean) {
    playerAssessments(assessmentId: $assessmentId, ageGroupIds: [$ageGroupId]) {
      _id
      isHeld
      isConcluded
      player {
        _id
        firstName
        lastName
      }
      position {
        _id
        name
      }
      adjustedScore
    }
    drills(companyId: $companyId, includeGlobal: $includeGlobal) {
      _id
      name
    }
    practicePlans(companyId: $companyId, includeGlobal: $includeGlobal) {
      _id
      name
    }
    ageGroup(_id: $ageGroupId) {
      _id
      name
      currentStage {
        generateGroupPlan {
          sessionStage {
            _id
            title
            config
          }
          sessionSets {
            session {
              _id
              location
              area
              address
              date
              start
            }
            group {
              _id
              position {
                _id
                name
              }
              player {
                _id
                firstName
                lastName
              }
            }
          }
        }
      }
    }
  }
`;

const FINALIZE_GROUPINGS = gql`
  mutation FinalizeGroupStage($_id: ID!, $sessionSets: [IFinalizeSessionInput!]!, $removedPlayerAssessmentIds: [ID!]) {
    finalizeGroupStage(_id: $_id, sessionSets: $sessionSets, removedPlayerAssessmentIds: $removedPlayerAssessmentIds) {
      currentStage {
        _id
        type
        config
        endsAt
      }
      nextStage {
        _id
        type
        config
        isReady
      }
    }
  }
`;

interface IAgeGroupingsGraphQL {
  ageGroupId: string;
  stage: IAssessmentStage;
}

const ApproveGroupingsGraphQL: React.FC<IAgeGroupingsGraphQL> = ({ ageGroupId, stage }) => {
  const user = getAuthUser();
  const [finalizeGroupStage] = useMutation(FINALIZE_GROUPINGS);
  const { data, loading, error } = useQuery(GET_APPROVE_GROUPINGS, {
    skip: !user,
    variables: {
      assessmentId: user?.settings.activeAssessmentId,
      ageGroupId,
      companyId: user?.settings.activeCompanyId,
      includeGlobal: user?.settings.includeGlobal
    },
    // Never a cached proposal: one made before the roster changed would leave the new players out.
    fetchPolicy: 'no-cache'
  });

  // Memoized on the response so the container rebuilds its sets only when a new plan arrives.
  const plans: IAssessmentStagePlan[] = useMemo(() => _.get(data, 'ageGroup.currentStage.generateGroupPlan') || [], [data]);
  const playerAssessments: IPlayerAssessment[] = useMemo(() => (data && data.playerAssessments) || [], [data]);
  const drills: IDrill[] = useMemo(() => (data && data.drills) || [], [data]);
  const practicePlans: IPracticePlan[] = useMemo(() => (data && data.practicePlans) || [], [data]);

  const handleFinalizeGruopStage = (sessionSets: IFinalizeSessionFields[], removedPlayerAssessmentIds: string[]) => {
    const sessions: IFinalizeSessionProps[] = sessionSets.map(sessionSet => ({
      _id: sessionSet._id,
      stageId: sessionSet.stageId,
      playerAssessmentIds: _.compact(_.map(sessionSet.playerAssessments, '_id')),
      drillIds: _.compact(_.map(sessionSet.drills, '_id')),
      practicePlanIds: _.compact(_.map(sessionSet.practicePlans, '_id'))
    }));

    return finalizeGroupStage({
      variables: { _id: stage._id, sessionSets: sessions, removedPlayerAssessmentIds },
      update: (cache, { data }) => {
        cache.modify({
          id: `AgeGroup:${ageGroupId}`,
          fields: {
            currentStage: () => data.finalizeGroupStage.currentStage,
            nextStage: () => data.finalizeGroupStage.nextStage
          }
        });

        sessions.map(session => {
          cache.modify({
            id: `AssessmentSession:${session._id}`,
            fields: {
              playerCount: () => session.playerAssessmentIds.length
            }
          })
        });
      }
    });
  }

  return <ApproveGroupingsContainer
    plans={plans}
    playerAssessments={playerAssessments}
    drills={drills}
    practicePlans={practicePlans}
    finalize={handleFinalizeGruopStage}
    loading={loading}
    fetchError={Boolean(error)}
    fetchErrorMessage={readUserInputMessage(error)}
  />;
};

export default ApproveGroupingsGraphQL;
